import React from 'react';

import { parseApiError, fieldLabel, firstFieldName, saveErrorText, type ParsedApiError } from './apiError';

/**
 * WP-03 — render a save failure so the user can do something about it.
 *
 * The owner's complaint was not that no message appeared. One did: "Failed to
 * save estimate". The complaint is that it was the SAME message whether the
 * customer was missing a street address, the phone had no signal, or the
 * server had genuinely broken — three situations with three different correct
 * responses from the user, collapsed into one dead end.
 *
 * So this splits them and says, for each, what to do next:
 *
 *   validation / incomplete  names the fields and moves the cursor to the first
 *   conflict                 says something changed and to reload
 *   offline / timeout        says the draft is safe and to press Save again
 *   system                   says it is not their fault, and gives the
 *                            reference to quote
 *
 * The "your work is still here" line is not reassurance padding. Every save
 * handler in this app keeps its form state on the error path, so the statement
 * is true — and the reason people stopped entering addresses was the belief
 * that a failed save cost them the draft.
 */

/** The subset of antd's message API used here. Narrow, so it is easy to fake. */
export interface MessageApiLike {
  error: (content: React.ReactNode, duration?: number) => unknown;
  warning: (content: React.ReactNode, duration?: number) => unknown;
}

export interface ReportSaveErrorOptions {
  /** The caller's own sentence, used only when the server said nothing useful. */
  fallback: string;
  /** Move the cursor to the first offending control, when the form can. */
  focusField?: (name: string) => void;
}

/**
 * Build the lines shown for a parsed error.
 *
 * Exported separately from the rendering so the wording is unit-testable
 * without mounting antd.
 */
export function saveErrorLines(parsed: ParsedApiError): string[] {
  const lines: string[] = [saveErrorText(parsed)];

  if ((parsed.kind === 'validation' || parsed.kind === 'incomplete') && parsed.fields.length > 0) {
    // The field list is repeated even when `detail` already names them: the
    // sentence is prose the user skims, the list is what they work through.
    for (const field of parsed.fields.slice(0, 6)) {
      lines.push(`• ${fieldLabel(field)} — ${field.message}`);
    }
    if (parsed.fields.length > 6) {
      lines.push(`• …and ${parsed.fields.length - 6} more`);
    }
  }

  if (parsed.kind === 'conflict') {
    lines.push('Reload to see the current version, then apply your change again.');
  }

  if (parsed.draftPreserved && parsed.kind !== 'validation' && parsed.kind !== 'incomplete') {
    lines.push('Nothing you typed has been lost.');
  }

  return lines;
}

/**
 * Classify, render, and focus.
 *
 * `warning` rather than `error` for the classes the user can fix: an amber
 * "more information needed" is a different instruction from a red "we broke",
 * and using red for both is how the two became indistinguishable.
 */
export function reportSaveError(
  messageApi: MessageApiLike,
  error: unknown,
  options: ReportSaveErrorOptions
): ParsedApiError {
  const parsed = parseApiError(error, options.fallback);
  const lines = saveErrorLines(parsed);

  const content = (
    <span>
      {lines.map((line, index) => (
        <React.Fragment key={line + index}>
          {index > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
    </span>
  );

  const userFixable = parsed.kind === 'validation' || parsed.kind === 'incomplete';
  // Longer for a field list than for a one-liner — a message that vanishes
  // before it has been read is the same as no message.
  const duration = userFixable && parsed.fields.length > 0 ? 10 : 6;

  if (userFixable) {
    messageApi.warning(content, duration);
  } else {
    messageApi.error(content, duration);
  }

  const focus = firstFieldName(parsed);
  if (focus && options.focusField) {
    try {
      options.focusField(focus);
    } catch {
      // A field the server names may not exist on this screen (a completeness
      // failure can name a customer column the estimate form does not render).
      // Failing to scroll must not swallow the message that was just shown.
    }
  }

  return parsed;
}
